import * as moment from "moment";
import { ModelAssumptionsUtil } from "src/app/core/v2/state/valuation-process/core/income/model-assumptions-util";
import { CostAssessmentRecordRow, DepreciatedRecordRow } from "../../types";
import { RecordOptions } from "./calculation";
import { normalizeRecords } from "./normalize";
import { recordsHasNullDepreciatedCost, totalCostOfRecords, totalDepreciatedCostOfRecords } from "./utils";

export interface RichardsCurveOptions {
  kind: 'richards-curve',
  economicLife: number,
  richardsSteepness: number,
  richardsInflection: number,
  richardsShape: number
}

interface RichardsCurveParameters {
  steepness: number,
  inflection: number,
  shape: number
}

export function richardsCurveMethod(
  records: CostAssessmentRecordRow[],
  recordOptions: RecordOptions,
  options: RichardsCurveOptions
) {
  const {
    richardsSteepness,
    richardsInflection,
    richardsShape,
    economicLife
  } = options
  const parameters: RichardsCurveParameters = {
    steepness: richardsSteepness,
    inflection: richardsInflection,
    shape: richardsShape
  }
  const {
    valuationDate,
    buildingConstructionDate,
    size
  } = recordOptions

  const _records = normalizeRecords(records, recordOptions)
  const _valuationDate = moment(valuationDate)
  const _constructionDate = moment(buildingConstructionDate)
  const yearDiff = ModelAssumptionsUtil._yearsDiff(
    _valuationDate,
    _constructionDate
  )

  const mappedRecords: DepreciatedRecordRow[] = _records.map(item => {
    const depreciatedCost = _depreciatedValue(
      item.total_cost,
      yearDiff,
      parameters
    )

    return {
      ...item,
      depreciated_cost: depreciatedCost,
      depreciated_per_unit: size && size !== 0 ? depreciatedCost / size : 0
    }
  })

  if (recordsHasNullDepreciatedCost(mappedRecords)) return null

  const totalCost = totalCostOfRecords(mappedRecords) 
  const totalDepreciatedCost = totalDepreciatedCostOfRecords(mappedRecords)

  return {
    records: mappedRecords,
    graphValues: _graphValues(economicLife, totalCost, parameters),
    totalCost,
    totalDepreciatedCost,
    yearDiff
  }
}

export function richardsCurve(
  totalCost: number,
  valuationDate: moment.Moment,
  constructionDate: moment.Moment,
  parameters: RichardsCurveParameters,
) {
  const yearDiff = ModelAssumptionsUtil._yearsDiff(
    valuationDate,
    constructionDate
  )
  return _depreciatedValue(
    totalCost,
    yearDiff,
    parameters,
  )
}

function _depreciatedValue(
  totalCost: number,
  years: number,
  parameters: RichardsCurveParameters
) {
  const {
    steepness,
    inflection,
    shape
  } = parameters
  if (
    shape === undefined || 
    shape === null || 
    shape === 0
  ) return null

  return totalCost * (1 - Math.pow(1 + Math.exp(-steepness * (years - inflection)), -(1/shape)))
}

function _graphValues(
  economicLife: number,
  totalCost: number,
  parameters: RichardsCurveParameters
) {
  const values: {
    age: number,
    depreciatedValue: number
    reducingBalancePerYear: number
  }[] = []
  for (let i = 0; i <= economicLife; i++) {
    const depreciatedValue = _depreciatedValue(totalCost, i, parameters)
    const reducingBalancePerYear = i == 0
      ? totalCost - depreciatedValue
      : values[i - 1].depreciatedValue - depreciatedValue

    values.push({
      age: i,
      depreciatedValue,
      reducingBalancePerYear
    })
  }
  return values
}